/**
 * The dashboard tile.
 *
 * The five hospital screens are worked by receptionists, clinic managers and
 * secretaries — people who did not pick this software and will not be trained
 * on it. Everything about this component follows from that: one big lit icon,
 * a plain-Arabic label under it, and a shape that obviously responds to a
 * finger.
 *
 * The depth is four CSS layers in one element (see `.tile3d` in
 * styles/index.css) rather than an icon set. It recolours from two custom
 * properties, stays sharp at any zoom, and adds nothing to the download —
 * which is the whole argument against shipping 3D artwork for this.
 *
 * The tone goes through a lookup rather than straight into the class string.
 * Not for Tailwind's sake — `.tone-cyan` and friends are hand-written rules in
 * the stylesheet, so the purge never touches them, unlike NAV_TINTS which is a
 * table of real Tailwind utilities for exactly that reason. The lookup is here
 * so an unrecognised tone falls back to cyan instead of emitting a class that
 * matches nothing and rendering a tile with no colour at all.
 */

import { Icon } from './index';

/** Known tones. An unknown one falls back rather than rendering uncoloured. */
const TONES = {
  cyan: 'tone-cyan',
  emerald: 'tone-emerald',
  amber: 'tone-amber',
  purple: 'tone-purple',
  rose: 'tone-rose',
  teal: 'tone-teal',
  blue: 'tone-blue',
  orange: 'tone-orange',
  red: 'tone-red',
  slate: 'tone-slate',
};

const toneClass = (tone) => TONES[tone] || TONES.cyan;

/**
 * A pressable tile: lit icon, label, optional hint.
 *
 * Renders as a real `<button>` when it does something, and a `<div>` when it
 * does not — so a keyboard reaches exactly the tiles that respond, and a
 * screen reader is not told that a decoration is actionable.
 */
export function Tile3D({
  icon,
  label,
  hint,
  tone = 'cyan',
  onClick,
  disabled = false,
  locked = false,
  pulse = false,
  enter = false,
  delay = 0,
  className = '',
  ...rest
}) {
  const interactive = Boolean(onClick) && !disabled;
  const Element = interactive ? 'button' : 'div';

  const classes = [
    'tile3d',
    toneClass(tone),
    interactive && 'tile3d--action',
    locked && 'tile3d--locked',
    pulse && 'tile3d--pulse',
    enter && 'tile3d--enter',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Element
      {...(interactive ? { type: 'button', onClick } : {})}
      disabled={interactive ? disabled : undefined}
      className={classes}
      // Capped so a long row still finishes arriving quickly — a wave that
      // takes two seconds to land reads as a slow screen, not a lively one.
      style={enter ? { animationDelay: `${Math.min(delay, 8) * 55}ms` } : undefined}
      {...rest}
    >
      <span className="tile3d__face">
        <Icon name={icon} size={26} className="tile3d__icon" />
      </span>
      <span className="tile3d__label">{label}</span>
      {hint && <span className="tile3d__hint">{hint}</span>}
      {locked && (
        <span className="absolute right-2 top-2 text-amber-300">
          <Icon name="lock" size={13} />
        </span>
      )}
    </Element>
  );
}

/**
 * The same tile carrying a number.
 *
 * The figure sits above the label because that is the order it is read in — a
 * manager glancing at a board wants the count first and the word second.
 */
export function StatTile3D({
  icon,
  label,
  value,
  hint,
  tone = 'cyan',
  onClick,
  enter = true,
  delay = 0,
  className = '',
}) {
  const interactive = Boolean(onClick);
  const Element = interactive ? 'button' : 'div';

  const classes = [
    'tile3d',
    toneClass(tone),
    interactive && 'tile3d--action',
    enter && 'tile3d--enter',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Element
      {...(interactive ? { type: 'button', onClick } : {})}
      className={classes}
      style={enter ? { animationDelay: `${Math.min(delay, 8) * 55}ms` } : undefined}
    >
      <span className="tile3d__face">
        <Icon name={icon} size={24} className="tile3d__icon" />
      </span>
      <span className="tile3d__value">{value}</span>
      <span className="tile3d__label">{label}</span>
      {hint && <span className="tile3d__hint">{hint}</span>}
    </Element>
  );
}

/**
 * The banner every hospital screen opens with.
 *
 * A plain sentence saying what this screen is for, in the second person. These
 * users arrive at a dashboard they did not ask for, and a title alone ("إدارة
 * العيادات") tells them what it is called rather than what to do with it.
 */
export function HeroBand({ icon, title, subtitle, tone = 'cyan', children }) {
  return (
    <div className={`hero-band mb-6 flex flex-wrap items-center gap-5 p-6 ${toneClass(tone)}`}>
      <span className="tile3d__face shrink-0">
        <Icon name={icon} size={30} className="tile3d__icon" />
      </span>

      <div className="min-w-[min(100%,16rem)] flex-1">
        <h1 className="text-xl font-bold text-white sm:text-2xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-300">{subtitle}</p>}
      </div>

      {/* The controls take their own row rather than squeezing the title into
          a column a word wide; on a wide screen they sit beside it. */}
      {children && <div className="flex max-w-full flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

/**
 * A numbered progress strip.
 *
 * Reception and the delay flow are both sequences, and a person who does this
 * job twice a day should not have to remember the order. Purely indicative —
 * nothing here gates anything.
 */
export function StepStrip({ steps, current }) {
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2">
      {steps.map((step, i) => (
        <span
          key={step}
          className={[
            'step-chip',
            i < current && 'step-chip--done',
            i === current && 'step-chip--active',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/10 text-[10px]">
            {i < current ? '✓' : i + 1}
          </span>
          {step}
        </span>
      ))}
    </div>
  );
}

export default Tile3D;
